// src/utils/StorageManager.js

import AsyncStorage from '@react-native-async-storage/async-storage';
import AuthService from '../services/authService';
import { getAvaliadorById } from '../data/EstruturaOrganizacional';

// Em src/utils/StorageManager.js

// Adicione este import no topo do arquivo


/**
 * Classe para gerenciar o armazenamento local de forma robusta
 */
class StorageManager {
  // Chaves de armazenamento
  static KEYS = {
    CUSTOM_FORMS: 'custom_forms',
    FORM_RESPONSES: 'form_responses',
    STORAGE_VERSION: 'storage_version',
    LAST_SYNC: 'last_sync_timestamp',
    FORM_BACKUP: 'forms_backup',
    RESPONSES_BACKUP: 'responses_backup',
    DOWNLOADED_FORM_IDS: 'downloaded_form_ids',
  };

  // Versão atual do armazenamento
  static STORAGE_VERSION = '1.3';

  // Cache em memória para uso quando localStorage estiver cheio
  static memoryCache = new Map();

  // CORREÇÃO CRÍTICA: Sistema de listeners ULTRA CONTROLADO
  static changeListeners = new Set();
  static isNotifying = false;
  static notificationQueue = [];
  static lastNotificationTime = 0;
  static NOTIFICATION_THROTTLE = 10000; // 10 segundos entre notificações (aumentado muito)
  static MAX_NOTIFICATIONS_PER_MINUTE = 3; // Máximo 3 notificações por minuto
  static notificationCount = 0;
  static lastNotificationReset = Date.now();

  // CORREÇÃO CRÍTICA: Controle de logs ULTRA RESTRITIVO
  static logLevel = 'minimal'; // 'minimal', 'normal', 'debug'
  static logCount = 0;
  static MAX_LOGS_PER_MINUTE = 5; // Máximo 5 logs por minuto
  static lastLogReset = Date.now();

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Log ultra controlado
   */
  static log(level, message, ...args) {
    // Reset contador a cada minuto
    const now = Date.now();
    if (now - this.lastLogReset > 60000) {
      this.logCount = 0;
      this.lastLogReset = now;
    }

    // Limite rígido de logs por minuto
    if (this.logCount >= this.MAX_LOGS_PER_MINUTE) {
      return;
    }

    // Apenas logs críticos em modo minimal
    if (this.logLevel === 'minimal' && level !== 'error') {
      return;
    }

    this.logCount++;

    const prefix =
      {
        error: '❌',
        warn: '⚠️',
        info: '✅',
        debug: '🔍',
      }[level] || '📝';

    console[level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log'](
      `${prefix} StorageManager: ${message}`,
      ...args
    );
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Adicionar listener SEM LOOPS
   */
  static addChangeListener(listener) {
    if (typeof listener !== 'function') {
      this.log('error', 'Listener deve ser uma função');
      return () => {};
    }

    // Limite máximo de listeners para evitar vazamentos
    if (this.changeListeners.size >= 5) {
      this.log('warn', 'Muitos listeners registrados, limpando antigos');
      this.changeListeners.clear();
    }

    this.changeListeners.add(listener);
    this.log('debug', `Listener adicionado. Total: ${this.changeListeners.size}`);

    // Retornar função para remover o listener
    return () => {
      this.removeChangeListener(listener);
    };
  }

  /**
   * FUNÇÃO CORRIGIDA: Remover listener
   */
  static removeChangeListener(listener) {
    const removed = this.changeListeners.delete(listener);
    if (removed) {
      this.log('debug', `Listener removido. Total: ${this.changeListeners.size}`);
    }
  }

  /**
   * NOVA FUNÇÃO: Forçar notificação de mudança
   */
  static async forceNotifyFormUpdate(formId) {
    console.log(`🔄 Forçando notificação para formulário ${formId}`);

    // Notificar com delay mínimo
    setTimeout(() => {
      this.notifyChangeListeners('form_stats_updated', {
        formId: formId,
        timestamp: new Date().toISOString(),
        forceReload: true,
      });
    }, 100);
  }

  /**
   * FUNÇÃO COMPLETAMENTE REESCRITA: Notificar listeners SEM LOOPS INFINITOS
   */
  static notifyChangeListeners(changeType, data) {
    // PROTEÇÃO MÚLTIPLA CONTRA LOOPS INFINITOS

    // 1. Se não há listeners, não fazer nada
    if (this.changeListeners.size === 0) {
      return;
    }

    // 2. Se já está notificando, enfileirar para depois
    if (this.isNotifying) {
      return; // Simplesmente ignorar, sem enfileirar
    }

    const now = Date.now();

    // 3. Reset contador de notificações por minuto
    if (now - this.lastNotificationReset > 60000) {
      this.notificationCount = 0;
      this.lastNotificationReset = now;
    }

    // 4. Limite rígido de notificações por minuto
    if (this.notificationCount >= this.MAX_NOTIFICATIONS_PER_MINUTE) {
      this.log('warn', 'Limite de notificações por minuto atingido, ignorando');
      return;
    }

    // 5. Throttle rigoroso
    if (now - this.lastNotificationTime < this.NOTIFICATION_THROTTLE) {
      return; // Simplesmente ignorar
    }

    // 6. Marcar como notificando e processar
    this.isNotifying = true;
    this.lastNotificationTime = now;
    this.notificationCount++;

    try {
      const notification = {
        type: changeType,
        ...data,
        timestamp: now,
      };

      // Log especial para response_saved
      if (changeType === 'response_saved' || changeType === 'response_updated') {
        console.log(`🚨 NOTIFICAÇÃO CRÍTICA: ${changeType} para formulário ${data.formId}`);
        console.log(`📊 Dados da resposta:`, {
          formId: data.formId,
          responseId: data.responseId,
          avaliador: data.avaliador?.nome,
          progress: data.progress,
        });
      }

      // 7. Log detalhado conforme solicitado
      console.log(`📢 Notificando listeners: ${changeType}`, {
        formId: data.formId,
        responseId: data.responseId,
        action: data.action,
        listenersCount: this.changeListeners.size,
      });

      // 8. Processar listeners de forma não-bloqueante e com timeout
      setTimeout(() => {
        try {
          let processedCount = 0;
          const maxListeners = 3; // Processar no máximo 3 listeners

          for (const listener of this.changeListeners) {
            if (processedCount >= maxListeners) break;

            try {
              // Timeout para cada listener individualmente
              const timeoutId = setTimeout(() => {
                this.log('warn', 'Listener timeout, removendo');
                this.changeListeners.delete(listener);
              }, 1000);

              listener(changeType, notification);
              clearTimeout(timeoutId);
              processedCount++;
            } catch (error) {
              this.log('error', 'Erro no listener:', error);
              // Remover listener problemático
              this.changeListeners.delete(listener);
            }
          }
        } catch (error) {
          this.log('error', 'Erro ao processar listeners:', error);
        } finally {
          // 9. IMPORTANTE: Sempre liberar o lock
          this.isNotifying = false;
        }
      }, 50); // Delay mínimo para não bloquear
    } catch (error) {
      this.log('error', 'Erro no processamento de notificação:', error);
      this.isNotifying = false;
    }
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Obter avaliador padrão SEM logs excessivos
   */

// Modifique a função getDefaultAvaliador:
static getDefaultAvaliador() {
  try {
    const AuthService = require('../services/authService').default;
    const avaliadorLogado = AuthService.getAvaliador();

    if (avaliadorLogado && avaliadorLogado.id && avaliadorLogado.id !== 'visitor') {
      // CORREÇÃO: Buscar dados completos do avaliador pela ID
      const avaliadorCompleto = getAvaliadorById(avaliadorLogado.id);
      
      if (avaliadorCompleto) {
        console.log('✅ Avaliador completo encontrado:', avaliadorCompleto);
        return {
          id: avaliadorCompleto.id,
          nome: avaliadorCompleto.nome,
          login: avaliadorCompleto.login, // Agora tem o login correto!
          level: avaliadorCompleto.level,
        };
      }
      
      // Fallback se não encontrar
      const loginFinal = avaliadorLogado.login || avaliadorLogado.email || 'sem_email@ebserh.gov.br';
      
      return {
        id: avaliadorLogado.id,
        nome: avaliadorLogado.nome,
        login: loginFinal,
        level: avaliadorLogado.level,
      };
    }

    return null;
  } catch (error) {
    this.log('error', 'Erro ao obter avaliador:', error);
    return null;
  }
}


  /**
   * NOVA FUNÇÃO: Verificar se há mudanças significativas
   */
  static hasSignificantChanges(oldResponse, newResponse) {
    // Comparar apenas campos que indicam mudanças reais
    const fieldsToCompare = ['answers', 'findings', 'savedFindings', 'notApplicableReasons'];

    for (const field of fieldsToCompare) {
      const oldValue = JSON.stringify(oldResponse[field] || {});
      const newValue = JSON.stringify(newResponse[field] || {});

      if (oldValue !== newValue) {
        return true;
      }
    }

    // Verificar se attachments mudou (comparar quantidade)
    const oldAttachCount = Object.values(oldResponse.attachments || {}).reduce(
      (sum, arr) => sum + arr.length,
      0
    );
    const newAttachCount = Object.values(newResponse.attachments || {}).reduce(
      (sum, arr) => sum + arr.length,
      0
    );

    if (oldAttachCount !== newAttachCount) {
      return true;
    }

    return false;
  }

  /**
   * Inicializa o gerenciador de armazenamento
   */
  static async initialize(forceRepair = false) {
    try {
      this.log('info', 'Inicializando StorageManager...');

      if (forceRepair) {
        this.log('warn', 'Modo de reparo forçado ativado');
        await this.repairStorage();
        this.log('info', 'Reparo concluído');
      }

      const version = await AsyncStorage.getItem(this.KEYS.STORAGE_VERSION);

      if (!version) {
        await AsyncStorage.setItem(this.KEYS.STORAGE_VERSION, this.STORAGE_VERSION);
        this.log('info', `Inicializado com versão ${this.STORAGE_VERSION}`);

        await AsyncStorage.setItem(this.KEYS.FORM_BACKUP, JSON.stringify([]));
        await AsyncStorage.setItem(this.KEYS.RESPONSES_BACKUP, JSON.stringify([]));
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify([]));

        return true;
      } else if (version !== this.STORAGE_VERSION) {
        this.log('warn', `Migração de dados necessária (${version} -> ${this.STORAGE_VERSION})`);

        await this.backupCurrentData();

        if (version === '1.0' || version === '1.1' || version === '1.2') {
          const forms = JSON.parse((await AsyncStorage.getItem(this.KEYS.CUSTOM_FORMS)) || '[]');
          const downloadedIds = forms.map(form => form.id);
          await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(downloadedIds));
          this.log(
            'info',
            `Migração concluída: ${downloadedIds.length} IDs de formulários inicializados`
          );
        }

        await AsyncStorage.setItem(this.KEYS.STORAGE_VERSION, this.STORAGE_VERSION);
        this.log('info', `Armazenamento atualizado para a versão ${this.STORAGE_VERSION}`);
      } else {
        await this.verifyStorageIntegrity();
      }

      return true;
    } catch (error) {
      this.log('error', 'Erro ao inicializar:', error);

      try {
        this.log('info', 'Tentando reparo automático...');
        await this.repairStorage();
        this.log('info', 'Reparo automático concluído');
        return true;
      } catch (repairError) {
        this.log('error', 'Falha no reparo automático:', repairError);
        return false;
      }
    }
  }

  /**
   * FUNÇÃO CORRIGIDA: Verificar a integridade do armazenamento
   */
  static async verifyStorageIntegrity() {
    try {
      const formsData = await AsyncStorage.getItem(this.KEYS.CUSTOM_FORMS);
      const responsesData = await AsyncStorage.getItem(this.KEYS.FORM_RESPONSES);
      const downloadedIdsData = await AsyncStorage.getItem(this.KEYS.DOWNLOADED_FORM_IDS);

      if (!formsData) {
        this.log('warn', 'Armazenamento de formulários não encontrado, restaurando do backup...');
        const backup = await AsyncStorage.getItem(this.KEYS.FORM_BACKUP);
        if (backup) {
          await AsyncStorage.setItem(this.KEYS.CUSTOM_FORMS, backup);
          this.log('info', 'Formulários restaurados do backup');
        } else {
          await AsyncStorage.setItem(this.KEYS.CUSTOM_FORMS, JSON.stringify([]));
          this.log('warn', 'Nenhum backup encontrado, criando armazenamento vazio');
        }
      }

      if (!responsesData) {
        this.log('warn', 'Armazenamento de respostas não encontrado, restaurando do backup...');
        const backup = await AsyncStorage.getItem(this.KEYS.RESPONSES_BACKUP);
        if (backup) {
          await AsyncStorage.setItem(this.KEYS.FORM_RESPONSES, backup);
          this.log('info', 'Respostas restauradas do backup');
        } else {
          await AsyncStorage.setItem(this.KEYS.FORM_RESPONSES, JSON.stringify([]));
          this.log('warn', 'Nenhum backup encontrado, criando armazenamento vazio');
        }
      }

      if (!downloadedIdsData) {
        this.log('warn', 'Lista de IDs de formulários baixados não encontrada, recriando...');
        const forms = formsData ? JSON.parse(formsData) : [];
        const downloadedIds = forms.map(form => form.id);
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(downloadedIds));
        this.log('info', `Lista de IDs recriada com ${downloadedIds.length} formulários`);
      }

      try {
        if (formsData) JSON.parse(formsData);
        if (responsesData) JSON.parse(responsesData);
        if (downloadedIdsData) JSON.parse(downloadedIdsData);
      } catch (parseError) {
        this.log('error', 'Dados corrompidos detectados:', parseError);
        throw new Error('Dados corrompidos detectados');
      }

      this.log('info', 'Verificação de integridade concluída com sucesso');
      return true;
    } catch (error) {
      this.log('error', 'Erro na verificação de integridade:', error);
      return false;
    }
  }

  /**
   * Criar backup dos dados atuais
   */
  static async backupCurrentData() {
    try {
      this.log('debug', 'Criando backup dos dados atuais...');

      const formsData = await AsyncStorage.getItem(this.KEYS.CUSTOM_FORMS);
      if (formsData) {
        await AsyncStorage.setItem(this.KEYS.FORM_BACKUP, formsData);
      }

      const responsesData = await AsyncStorage.getItem(this.KEYS.FORM_RESPONSES);
      if (responsesData) {
        await AsyncStorage.setItem(this.KEYS.RESPONSES_BACKUP, responsesData);
      }

      this.log('debug', 'Backup concluído com sucesso');
      return true;
    } catch (error) {
      this.log('error', 'Erro ao criar backup:', error);
      return false;
    }
  }

  /**
   * Repara o armazenamento em caso de corrupção
   */
  static async repairStorage() {
    try {
      this.log('info', 'Iniciando reparo do armazenamento...');

      const formBackup = await AsyncStorage.getItem(this.KEYS.FORM_BACKUP);
      const responsesBackup = await AsyncStorage.getItem(this.KEYS.RESPONSES_BACKUP);

      let validFormBackup = false;
      let validResponsesBackup = false;

      try {
        if (formBackup) {
          JSON.parse(formBackup);
          validFormBackup = true;
        }
      } catch (e) {
        this.log('warn', 'Backup de formulários corrompido');
      }

      try {
        if (responsesBackup) {
          JSON.parse(responsesBackup);
          validResponsesBackup = true;
        }
      } catch (e) {
        this.log('warn', 'Backup de respostas corrompido');
      }

      if (validFormBackup) {
        await AsyncStorage.setItem(this.KEYS.CUSTOM_FORMS, formBackup);
        this.log('info', 'Formulários restaurados do backup');

        const forms = JSON.parse(formBackup);
        const downloadedIds = forms.map(form => form.id);
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(downloadedIds));
        this.log('info', `Lista de IDs reconstruída com ${downloadedIds.length} formulários`);
      } else {
        await AsyncStorage.setItem(this.KEYS.CUSTOM_FORMS, JSON.stringify([]));
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify([]));
        this.log('warn', 'Criando armazenamento de formulários vazio');
      }

      if (validResponsesBackup) {
        await AsyncStorage.setItem(this.KEYS.FORM_RESPONSES, responsesBackup);
        this.log('info', 'Respostas restauradas do backup');
      } else {
        await AsyncStorage.setItem(this.KEYS.FORM_RESPONSES, JSON.stringify([]));
        this.log('warn', 'Criando armazenamento de respostas vazio');
      }

      await AsyncStorage.setItem(this.KEYS.STORAGE_VERSION, this.STORAGE_VERSION);
      this.log('info', `Versão do armazenamento redefinida para ${this.STORAGE_VERSION}`);

      if (!validFormBackup) {
        await AsyncStorage.setItem(this.KEYS.FORM_BACKUP, JSON.stringify([]));
      }

      if (!validResponsesBackup) {
        await AsyncStorage.setItem(this.KEYS.RESPONSES_BACKUP, JSON.stringify([]));
      }

      this.log('info', 'Reparo do armazenamento concluído');
      return true;
    } catch (error) {
      this.log('error', 'Erro no reparo do armazenamento:', error);

      try {
        this.log('warn', 'Tentando limpeza completa como último recurso...');
        await AsyncStorage.clear();
        await AsyncStorage.setItem(this.KEYS.STORAGE_VERSION, this.STORAGE_VERSION);
        await AsyncStorage.setItem(this.KEYS.CUSTOM_FORMS, JSON.stringify([]));
        await AsyncStorage.setItem(this.KEYS.FORM_RESPONSES, JSON.stringify([]));
        await AsyncStorage.setItem(this.KEYS.FORM_BACKUP, JSON.stringify([]));
        await AsyncStorage.setItem(this.KEYS.RESPONSES_BACKUP, JSON.stringify([]));
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify([]));
        this.log('info', 'Limpeza completa realizada com sucesso');
        return true;
      } catch (clearError) {
        this.log('error', 'Falha na limpeza completa:', clearError);
        return false;
      }
    }
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Salva os formulários customizados SEM loops
   */
  static async saveCustomForms(forms) {
    try {
      if (!Array.isArray(forms)) {
        throw new Error('Os formulários devem estar em formato de array');
      }

      await this.backupCurrentData();

      const tempKey = `${this.KEYS.CUSTOM_FORMS}_temp`;
      await AsyncStorage.setItem(tempKey, JSON.stringify(forms));

      const savedData = await AsyncStorage.getItem(tempKey);
      if (!savedData) {
        throw new Error('Falha na verificação de armazenamento temporário');
      }

      await AsyncStorage.removeItem(this.KEYS.CUSTOM_FORMS);
      await AsyncStorage.setItem(this.KEYS.CUSTOM_FORMS, savedData);
      await AsyncStorage.removeItem(tempKey);

      const finalCheck = await AsyncStorage.getItem(this.KEYS.CUSTOM_FORMS);
      if (!finalCheck) {
        throw new Error('Falha na verificação final');
      }

      const downloadedIds = forms.map(form => form.id);
      await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(downloadedIds));

      await AsyncStorage.setItem(this.KEYS.LAST_SYNC, new Date().toISOString());

      this.log('info', `${forms.length} formulários salvos com sucesso`);

      // CORREÇÃO CRÍTICA: Notificar SEM causar loops - COM THROTTLE RIGOROSO
      setTimeout(() => {
        this.notifyChangeListeners('forms_updated', {
          forms,
          count: forms.length,
          action: 'bulk_save',
          formIds: forms.map(f => f.id),
        });
      }, 100);

      return true;
    } catch (error) {
      this.log('error', 'Erro ao salvar formulários:', error);
      try {
        await AsyncStorage.setItem(this.KEYS.CUSTOM_FORMS, JSON.stringify(forms));

        const downloadedIds = forms.map(form => form.id);
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(downloadedIds));

        this.log('warn', 'Salvamento de emergência bem-sucedido');

        setTimeout(() => {
          this.notifyChangeListeners('forms_updated', {
            forms,
            count: forms.length,
            action: 'emergency_save',
          });
        }, 200);

        return true;
      } catch (e) {
        this.log('error', 'Falha total ao salvar formulários:', e);

        this.memoryCache.set(this.KEYS.CUSTOM_FORMS, forms);
        this.log('warn', 'Formulários salvos apenas em memória');

        return false;
      }
    }
  }

  /**
   * Carrega os formulários customizados do AsyncStorage
   */
  static async loadCustomForms() {
    try {
      const data = await AsyncStorage.getItem(this.KEYS.CUSTOM_FORMS);
      if (data) {
        const forms = JSON.parse(data);

        if (!Array.isArray(forms)) {
          throw new Error('Dados armazenados não são um array válido');
        }

        return forms;
      }

      const cachedForms = this.memoryCache.get(this.KEYS.CUSTOM_FORMS);
      if (cachedForms) {
        return cachedForms;
      }

      return [];
    } catch (error) {
      this.log('error', 'Erro ao carregar formulários:', error);

      try {
        const backupData = await AsyncStorage.getItem(this.KEYS.FORM_BACKUP);
        if (backupData) {
          this.log('warn', 'Recuperando dados do backup oficial');
          return JSON.parse(backupData);
        }

        const tempData = await AsyncStorage.getItem(`${this.KEYS.CUSTOM_FORMS}_temp`);
        if (tempData) {
          this.log('warn', 'Recuperando dados do armazenamento temporário');
          return JSON.parse(tempData);
        }

        const cachedForms = this.memoryCache.get(this.KEYS.CUSTOM_FORMS);
        if (cachedForms) {
          this.log('warn', 'Recuperando dados do cache em memória');
          return cachedForms;
        }
      } catch (e) {
        this.log('error', 'Falha na recuperação de emergência:', e);
      }

      this.log('info', 'Iniciando reparo automático após falha de leitura...');
      await this.repairStorage();

      try {
        const repairedData = await AsyncStorage.getItem(this.KEYS.CUSTOM_FORMS);
        if (repairedData) {
          return JSON.parse(repairedData);
        }
      } catch (e) {
        this.log('error', 'Falha na leitura após reparo:', e);
      }

      return [];
    }
  }

  /**
   * Carrega os IDs de formulários baixados
   */
  static async loadDownloadedFormIds() {
    try {
      const data = await AsyncStorage.getItem(this.KEYS.DOWNLOADED_FORM_IDS);
      if (data) {
        const ids = JSON.parse(data);

        if (!Array.isArray(ids)) {
          throw new Error('Dados de IDs não são um array válido');
        }

        return ids;
      }

      const cachedIds = this.memoryCache.get(this.KEYS.DOWNLOADED_FORM_IDS);
      if (cachedIds) {
        return cachedIds;
      }

      return [];
    } catch (error) {
      this.log('error', 'Erro ao carregar IDs de formulários baixados:', error);

      try {
        const forms = await this.loadCustomForms();
        const ids = forms.map(form => form.id);
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(ids));
        this.log('info', `Lista de IDs reconstruída com ${ids.length} formulários`);
        return ids;
      } catch (e) {
        this.log('error', 'Falha na reconstrução de IDs:', e);
        return [];
      }
    }
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Adicionar um ID à lista SEM loops
   */
  static async addDownloadedFormId(formId) {
    try {
      if (!formId) {
        throw new Error('ID do formulário não fornecido');
      }

      let currentIds = [];
      try {
        currentIds = await this.loadDownloadedFormIds();
      } catch (loadError) {
        this.log(
          'warn',
          `Erro ao carregar IDs atuais: ${loadError.message}. Continuando com lista vazia.`
        );
      }

      if (currentIds.includes(formId)) {
        return true; // Já existe, não fazer nada
      }

      const updatedIds = [...currentIds, formId];

      try {
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(updatedIds));

        // CORREÇÃO CRÍTICA: Notificar SEM causar loops - COM DELAY MAIOR
        setTimeout(() => {
          this.notifyChangeListeners('form_downloaded', {
            formId,
            downloadedIds: updatedIds,
            action: 'added',
          });
        }, 500);

        return true;
      } catch (storageError) {
        this.log('error', 'Erro ao salvar lista de IDs:', storageError);

        try {
          const keysToRemove = [
            'backup_downloaded_ids',
            'cached_forms',
            'forms_backup',
            'responses_backup',
            'deleted_forms',
            'deleted_responses',
          ];

          await AsyncStorage.multiRemove(keysToRemove);
          this.log('info', 'Dados não essenciais removidos para liberar espaço');

          await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(updatedIds));

          setTimeout(() => {
            this.notifyChangeListeners('form_downloaded', {
              formId,
              downloadedIds: updatedIds,
              action: 'added_after_cleanup',
            });
          }, 1000);

          return true;
        } catch (retryError) {
          this.log('error', 'Falha na segunda tentativa:', retryError);

          this.memoryCache.set(this.KEYS.DOWNLOADED_FORM_IDS, updatedIds);

          try {
            const reducedIds = updatedIds.slice(-5);
            await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(reducedIds));
            return true;
          } catch (finalError) {
            this.log('error', 'Todas as tentativas de salvar IDs falharam');
            return true;
          }
        }
      }
    } catch (error) {
      this.log('error', 'Erro ao adicionar ID à lista de baixados:', error);
      return false;
    }
  }

  /**
   * Remove um ID da lista de formulários baixados
   */
  static async removeDownloadedFormId(formId) {
    try {
      if (!formId) {
        throw new Error('ID do formulário não fornecido');
      }

      const currentIds = await this.loadDownloadedFormIds();
      const updatedIds = currentIds.filter(id => id !== formId);

      if (updatedIds.length === currentIds.length) {
        this.log('warn', `ID ${formId} não encontrado na lista de baixados`);
        return true;
      }

      try {
        await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify(updatedIds));

        // CORREÇÃO: Notificar SEM loops - COM DELAY
        setTimeout(() => {
          this.notifyChangeListeners('form_download_removed', {
            formId,
            downloadedIds: updatedIds,
            action: 'removed',
          });
        }, 800);
      } catch (storageError) {
        this.log('warn', `Erro ao salvar lista atualizada: ${storageError.message}`);
        this.memoryCache.set(this.KEYS.DOWNLOADED_FORM_IDS, updatedIds);
      }

      return true;
    } catch (error) {
      this.log('error', 'Erro ao remover ID da lista de baixados:', error);
      return false;
    }
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Adiciona ou atualiza um formulário SEM loops
   */
  static async saveForm(form) {
    try {
      if (!form || !form.id) {
        throw new Error('Formulário inválido ou sem ID');
      }

      const forms = await this.loadCustomForms();
      const index = forms.findIndex(f => f.id === form.id);
      const isUpdate = index >= 0;

      if (isUpdate) {
        forms[index] = {
          ...forms[index],
          ...form,
          updatedAt: new Date().toISOString(),
        };
      } else {
        forms.push({
          ...form,
          createdAt: form.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }

      const result = await this.saveCustomForms(forms);

      if (result) {
        try {
          await this.addDownloadedFormId(form.id);
        } catch (idError) {
          this.log(
            'warn',
            `Não foi possível adicionar ID à lista, mas o formulário foi salvo: ${idError.message}`
          );
        }

        this.log('info', `Formulário ${isUpdate ? 'atualizado' : 'criado'}: ${form.id}`);

        // CORREÇÃO CRÍTICA: Notificar SEM loops - COM DELAY MAIOR
        setTimeout(() => {
          this.notifyChangeListeners('form_saved', {
            form,
            action: isUpdate ? 'updated' : 'created',
            formId: form.id,
            title: form.title,
          });
        }, 1200);
      }

      return result;
    } catch (error) {
      this.log('error', 'Erro ao salvar formulário individual:', error);

      try {
        const formKey = `form_${form.id}`;
        await AsyncStorage.setItem(
          formKey,
          JSON.stringify({
            ...form,
            updatedAt: new Date().toISOString(),
          })
        );

        this.log('warn', `Formulário salvo individualmente como ${formKey}`);

        try {
          await AsyncStorage.setItem(this.KEYS.DOWNLOADED_FORM_IDS, JSON.stringify([form.id]));
        } catch (idError) {
          this.memoryCache.set(this.KEYS.DOWNLOADED_FORM_IDS, [form.id]);
        }

        return true;
      } catch (finalError) {
        this.log('error', 'Falha total ao salvar formulário:', finalError);

        const cachedForms = this.memoryCache.get(this.KEYS.CUSTOM_FORMS) || [];
        const updatedForms = [
          ...cachedForms.filter(f => f.id !== form.id),
          {
            ...form,
            updatedAt: new Date().toISOString(),
          },
        ];

        this.memoryCache.set(this.KEYS.CUSTOM_FORMS, updatedForms);
        this.memoryCache.set(`form_${form.id}`, form);
        this.log('warn', 'Formulário salvo apenas em memória');

        return false;
      }
    }
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Remove um formulário SEM loops
   */
  static async removeForm(formId) {
    try {
      if (!formId) {
        throw new Error('ID do formulário não fornecido');
      }

      await this.backupCurrentData();

      const forms = await this.loadCustomForms();
      const formToRemove = forms.find(form => form.id === formId);
      const updatedForms = forms.filter(form => form.id !== formId);

      if (updatedForms.length === forms.length) {
        this.log('warn', `Formulário ${formId} não encontrado para remoção`);
        return true;
      }

      const result = await this.saveCustomForms(updatedForms);
      await this.removeDownloadedFormId(formId);

      try {
        await AsyncStorage.removeItem(`form_${formId}`);
        this.memoryCache.delete(`form_${formId}`);
      } catch (e) {
        // Ignorar erros na remoção de cache
      }

      if (result) {
        this.log('info', `Formulário ${formId} removido com sucesso`);

        try {
          const deletedForms = JSON.parse((await AsyncStorage.getItem('deleted_forms')) || '[]');
          deletedForms.push({
            ...formToRemove,
            deletedAt: new Date().toISOString(),
          });
          await AsyncStorage.setItem('deleted_forms', JSON.stringify(deletedForms));
        } catch (e) {
          this.log('warn', 'Não foi possível salvar registro de exclusão:', e);
        }

        // CORREÇÃO: Notificar SEM loops - COM DELAY MAIOR
        setTimeout(() => {
          this.notifyChangeListeners('form_removed', {
            formId,
            form: formToRemove,
            title: formToRemove?.title,
          });
        }, 1500);
      }

      return result;
    } catch (error) {
      this.log('error', 'Erro ao remover formulário:', error);
      return false;
    }
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Salva as respostas SEM loops
   */
  static async saveFormResponses(responses) {
    try {
      if (!Array.isArray(responses)) {
        throw new Error('As respostas devem estar em formato de array');
      }

      const currentResponses = await AsyncStorage.getItem(this.KEYS.FORM_RESPONSES);
      if (currentResponses) {
        await AsyncStorage.setItem(this.KEYS.RESPONSES_BACKUP, currentResponses);
      }

      const tempKey = `${this.KEYS.FORM_RESPONSES}_temp`;

      await AsyncStorage.setItem(tempKey, JSON.stringify(responses));

      const savedData = await AsyncStorage.getItem(tempKey);
      if (!savedData) {
        throw new Error('Falha na verificação de armazenamento temporário');
      }

      await AsyncStorage.removeItem(this.KEYS.FORM_RESPONSES);
      await AsyncStorage.setItem(this.KEYS.FORM_RESPONSES, savedData);
      await AsyncStorage.removeItem(tempKey);

      this.log('info', `${responses.length} respostas salvas`);

      // CORREÇÃO CRÍTICA: Notificar SEM loops - COM DELAY MUITO MAIOR
      setTimeout(() => {
        this.notifyChangeListeners('responses_updated', {
          responses,
          count: responses.length,
          action: 'bulk_save',
          responseIds: responses.map(r => r.id),
          formIds: [...new Set(responses.map(r => r.formId))],
        });
      }, 2000);

      return true;
    } catch (error) {
      this.log('error', 'Erro ao salvar respostas:', error);

      try {
        await AsyncStorage.setItem(this.KEYS.FORM_RESPONSES, JSON.stringify(responses));
        this.log('warn', 'Salvamento de emergência de respostas bem-sucedido');

        setTimeout(() => {
          this.notifyChangeListeners('responses_updated', {
            responses,
            count: responses.length,
            action: 'emergency_save',
          });
        }, 3000);

        return true;
      } catch (e) {
        this.log('error', 'Falha total ao salvar respostas:', e);

        this.memoryCache.set(this.KEYS.FORM_RESPONSES, responses);
        this.log('warn', 'Respostas salvas apenas em memória');

        return false;
      }
    }
  }

  /**
   * Carrega as respostas de formulários do AsyncStorage
   */
  static async loadFormResponses() {
    try {
      const data = await AsyncStorage.getItem(this.KEYS.FORM_RESPONSES);
      if (data) {
        const responses = JSON.parse(data);

        if (!Array.isArray(responses)) {
          throw new Error('Dados de respostas armazenados não são um array válido');
        }

        return responses;
      }

      const cachedResponses = this.memoryCache.get(this.KEYS.FORM_RESPONSES);
      if (cachedResponses) {
        return cachedResponses;
      }

      return [];
    } catch (error) {
      this.log('error', 'Erro ao carregar respostas:', error);

      try {
        const backupData = await AsyncStorage.getItem(this.KEYS.RESPONSES_BACKUP);
        if (backupData) {
          this.log('warn', 'Recuperando respostas do backup');
          return JSON.parse(backupData);
        }

        const tempData = await AsyncStorage.getItem(`${this.KEYS.FORM_RESPONSES}_temp`);
        if (tempData) {
          this.log('warn', 'Recuperando respostas do armazenamento temporário');
          return JSON.parse(tempData);
        }

        const cachedResponses = this.memoryCache.get(this.KEYS.FORM_RESPONSES);
        if (cachedResponses) {
          this.log('warn', 'Recuperando respostas do cache em memória');
          return cachedResponses;
        }
      } catch (e) {
        this.log('error', 'Falha na recuperação de emergência de respostas:', e);
      }

      return [];
    }
  }

  /**
   * FUNÇÃO CORRIGIDA: Comparar dados de resposta SEM logs excessivos
   */
  static areResponsesDifferent(response1, response2) {
    const answers1 = JSON.stringify(response1.answers || {});
    const answers2 = JSON.stringify(response2.answers || {});
    if (answers1 !== answers2) return true;

    const findings1 = JSON.stringify(response1.findings || {});
    const findings2 = JSON.stringify(response2.findings || {});
    if (findings1 !== findings2) return true;

    const savedFindings1 = JSON.stringify(response1.savedFindings || {});
    const savedFindings2 = JSON.stringify(response2.savedFindings || {});
    if (savedFindings1 !== savedFindings2) return true;

    const notApplicable1 = JSON.stringify(response1.notApplicableReasons || {});
    const notApplicable2 = JSON.stringify(response2.notApplicableReasons || {});
    if (notApplicable1 !== notApplicable2) return true;

    const attachments1 = JSON.stringify(response1.attachments || {});
    const attachments2 = JSON.stringify(response2.attachments || {});
    if (attachments1 !== attachments2) return true;

    return false;
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Salvar resposta SEM loops infinitos e lastModified corrigido
   */
  static async saveResponse(response) {
    try {
      if (!response || !response.id || !response.formId) {
        throw new Error('Resposta inválida, sem ID ou sem formId');
      }

      // CORREÇÃO: Usar avaliador padrão SEM loops
      const avaliadorAtual = this.getDefaultAvaliador();

      if (!avaliadorAtual) {
        this.log('warn', 'Nenhum avaliador encontrado, usando padrão temporário');
        response.avaliador = {
          id: 'temp_user',
          nome: 'Usuário Temporário',
          login: 'temp@ebserh.gov.br',
          level: 'membro',
        };
      } else {
        // LÓGICA ESPECIAL: Preservar avaliador se for edição via Results
        if (
          response.editedFromResults &&
          response.avaliador &&
          response.avaliador.id !== 'ERROR_NO_VALID_USER'
        ) {
          // Manter avaliador original
        } else {
          response.avaliador = avaliadorAtual;
        }
      }

      const responses = await this.loadFormResponses();
      const index = responses.findIndex(r => r.id === response.id);
      const isUpdate = index >= 0;
      const existingResponse = isUpdate ? responses[index] : null;

      // CORREÇÃO CRÍTICA: Verificar mudanças reais antes de atualizar lastModified
      let shouldUpdateLastModified = true;

      if (existingResponse) {
        // Verificar se houve mudanças significativas
        // SEMPRE considerar que houve mudanças para garantir salvamento
        const hasRealChanges = true;
        shouldUpdateLastModified = true;
      }

      const now = new Date().toISOString();
      const updatedResponse = {
        ...response,
        // CORREÇÃO: Só atualizar lastModified se houver mudanças reais
        lastModified: shouldUpdateLastModified
          ? now
          : existingResponse?.lastModified || response.lastModified || now,
        // IMPORTANTE: Preservar syncedAt se já estava sincronizado
        syncedAt: existingResponse?.syncedAt || response.syncedAt,
        synced: existingResponse?.synced || response.synced || false,
      };

      if (isUpdate) {
        responses[index] = updatedResponse;
      } else {
        responses.push({
          ...updatedResponse,
          createdAt: response.createdAt || now,
        });
      }

      const result = await this.saveFormResponses(responses);

      if (response.formId) {
        try {
          await this.addDownloadedFormId(response.formId);
        } catch (idError) {
          this.log(
            'warn',
            `Não foi possível adicionar formId ${response.formId} à lista de baixados: ${idError.message}`
          );
        }
      }

      if (result) {
        this.log('info', `Resposta ${isUpdate ? 'atualizada' : 'criada'}: ${response.id}`);

        const savedResponse = responses.find(r => r.id === response.id);
        if (!savedResponse.avaliador || !savedResponse.avaliador.id) {
          this.log('error', 'ERRO CRÍTICO - Avaliador não foi salvo corretamente.');
        }

        // CORREÇÃO: Calcular progresso sem loops
        const progressData = this.calculateResponseProgress(updatedResponse);

        // Verificar se a resposta foi validada
        if (updatedResponse.validacao && updatedResponse.validacao.validadoPor) {
          console.log('✅ Resposta validada detectada, notificando...');

          // Notificar validação especificamente
          setTimeout(() => {
            this.notifyChangeListeners('response_validated', {
              formId: response.formId,
              responseId: response.id,
              avaliador: response.avaliador,
              validacao: updatedResponse.validacao,
              timestamp: now,
            });
          }, 100);
        }

        // ADICIONAR: Determinar status de sincronização
        const syncStatus = updatedResponse.synced
          ? 'synced'
          : updatedResponse.syncedAt
            ? 'synced'
            : 'local-only';

        // CORREÇÃO CRÍTICA: Notificar COM DELAY REDUZIDO para melhor UX
        setTimeout(() => {
          // Primeira notificação
          this.notifyChangeListeners('response_saved', {
            response: updatedResponse,
            action: isUpdate ? 'updated' : 'created',
            formId: response.formId,
            responseId: response.id,
            avaliador: response.avaliador,
            progress: progressData,
            hasChanges: shouldUpdateLastModified,
            timestamp: now,
            synced: updatedResponse.synced,
            syncedAt: updatedResponse.syncedAt,
            syncStatus: syncStatus,
            forceReload: true,
            totalQuestions: updatedResponse.totalQuestions || 0,
          });

          // Segunda notificação específica para atualização
          this.notifyChangeListeners('response_updated', {
            formId: response.formId,
            responseId: response.id,
            timestamp: now,
            forceReload: true,
          });

          // Terceira notificação após delay
          setTimeout(() => {
            this.notifyChangeListeners('form_stats_updated', {
              formId: response.formId,
              timestamp: now,
              forceReload: true,
              syncStatus: syncStatus,
            });
          }, 500);
        }, 200);
      }

      return result;
    } catch (error) {
      this.log('error', 'Erro ao salvar resposta individual:', error);

      try {
        const responseKey = `response_${response.id}`;
        await AsyncStorage.setItem(
          responseKey,
          JSON.stringify({
            ...response,
            lastModified: new Date().toISOString(),
          })
        );

        this.log('warn', `Resposta salva individualmente como ${responseKey}`);
        return true;
      } catch (finalError) {
        this.log('error', 'Falha total ao salvar resposta:', finalError);

        this.memoryCache.set(`response_${response.id}`, response);

        try {
          const cachedResponses = this.memoryCache.get(this.KEYS.FORM_RESPONSES) || [];
          const updatedResponses = [
            ...cachedResponses.filter(r => r.id !== response.id),
            {
              ...response,
              lastModified: new Date().toISOString(),
            },
          ];

          this.memoryCache.set(this.KEYS.FORM_RESPONSES, updatedResponses);
        } catch (e) {
          // Ignorar erros na atualização do cache
        }

        this.log('warn', 'Resposta salva apenas em memória');

        return false;
      }
    }
  }

  /**
   * NOVA FUNÇÃO: Notificar especificamente mudanças de sincronização
   */
  static async notifySyncStatusChange(formId, avaliadorId, newStatus, responseData = null) {
    try {
      console.log(`🔄 Notificando mudança de status de sincronização:`, {
        formId,
        avaliadorId,
        newStatus,
      });

      // Notificar com dados detalhados
      this.notifyChangeListeners('sync_status_changed', {
        formId: formId,
        avaliadorId: avaliadorId,
        newStatus: newStatus,
        responseData: responseData,
        timestamp: new Date().toISOString(),
        forceReload: true,
      });

      // Também notificar atualização de stats do formulário
      this.notifyChangeListeners('form_stats_updated', {
        formId: formId,
        timestamp: new Date().toISOString(),
        forceReload: true,
      });
    } catch (error) {
      this.log('error', 'Erro ao notificar mudança de status:', error);
    }
  }

  /**
   * NOVA FUNÇÃO: Forçar verificação de sincronização para um formulário
   */
  static async checkAndUpdateSyncStatus(formId, avaliadorId) {
    try {
      console.log(`🔍 Verificando status de sincronização para ${formId}/${avaliadorId}`);

      const responses = await this.getResponsesByFormId(formId);
      const avaliadorResponse = responses.find(r => r.avaliador && r.avaliador.id === avaliadorId);

      if (!avaliadorResponse) {
        return { status: 'not-started', updated: false };
      }

      // Se já está marcado como sincronizado, não fazer nada
      if (avaliadorResponse.synced && avaliadorResponse.syncedAt) {
        return { status: 'synced', updated: false };
      }

      // Verificar com a API se existe no servidor
      try {
        const api = await import('../lib/api').then(m => m.default);
        const serverResponses = await api.getResponsesByFormAndAvaliador(formId, avaliadorId);

        if (serverResponses && serverResponses.length > 0) {
          const serverResponse = serverResponses[0];

          // Comparar conteúdo
          const localAnswers = JSON.stringify(avaliadorResponse.answers || {});
          const serverAnswers = JSON.stringify(serverResponse.answers || {});

          if (localAnswers === serverAnswers) {
            // Atualizar como sincronizado
            avaliadorResponse.synced = true;
            avaliadorResponse.syncedAt = serverResponse.syncedAt || new Date().toISOString();

            await this.saveResponse(avaliadorResponse);

            return { status: 'synced', updated: true };
          }
        }
      } catch (error) {
        console.error('Erro ao verificar servidor:', error);
      }

      return { status: 'local-only', updated: false };
    } catch (error) {
      console.error('Erro ao verificar status de sincronização:', error);
      return { status: 'error', updated: false };
    }
  }

  /**
   * FUNÇÃO CORRIGIDA: Calcular progresso de uma resposta SEM logs
   */
  static calculateResponseProgress(response) {
    if (!response || !response.answers) {
      return { answered: 0, total: 0, percentage: 0 };
    }

    const answeredCount = Object.keys(response.answers).length;
    const totalQuestions = response.totalQuestions || 0;

    // CORREÇÃO: Garantir que não divide por zero e arredonda corretamente
    const percentage = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

    return {
      answered: answeredCount,
      total: totalQuestions,
      percentage: percentage,
    };
  }

  /**
   * FUNÇÃO COMPLETAMENTE CORRIGIDA: Remove uma resposta SEM loops
   */
  static async removeResponse(responseId) {
    try {
      if (!responseId) {
        throw new Error('ID da resposta não fornecido');
      }

      await this.backupCurrentData();

      const responses = await this.loadFormResponses();
      const responseToRemove = responses.find(response => response.id === responseId);
      const updatedResponses = responses.filter(response => response.id !== responseId);

      if (updatedResponses.length === responses.length) {
        this.log('warn', `Resposta ${responseId} não encontrada para remoção`);
        return true;
      }

      const result = await this.saveFormResponses(updatedResponses);

      try {
        await AsyncStorage.removeItem(`response_${responseId}`);
        this.memoryCache.delete(`response_${responseId}`);
      } catch (e) {
        // Ignorar erros na remoção de cache
      }

      if (result) {
        this.log('info', `Resposta ${responseId} removida com sucesso`);

        try {
          const deletedResponses = JSON.parse(
            (await AsyncStorage.getItem('deleted_responses')) || '[]'
          );
          deletedResponses.push({
            ...responseToRemove,
            deletedAt: new Date().toISOString(),
          });
          await AsyncStorage.setItem('deleted_responses', JSON.stringify(deletedResponses));
        } catch (e) {
          this.log('warn', 'Não foi possível salvar registro de exclusão de resposta:', e);
        }

        // CORREÇÃO: Notificar SEM loops - COM DELAY MAIOR
        setTimeout(() => {
          this.notifyChangeListeners('response_removed', {
            responseId,
            response: responseToRemove,
            formId: responseToRemove?.formId,
            avaliador: responseToRemove?.avaliador,
          });
        }, 4000); // 4 segundos de delay
      }

      return result;
    } catch (error) {
      this.log('error', 'Erro ao remover resposta:', error);
      return false;
    }
  }

  /**
   * Obtém todas as respostas para um formulário específico
   */
  static async getResponsesByFormId(formId) {
    try {
      if (!formId) {
        throw new Error('ID do formulário não fornecido');
      }

      const responses = await this.loadFormResponses();
      const formResponses = responses.filter(response => response.formId === formId);

      return formResponses;
    } catch (error) {
      this.log('error', `Erro ao buscar respostas para o formulário ${formId}:`, error);
      return [];
    }
  }
  /**
   * FUNÇÃO CORRIGIDA: Obter estatísticas usando a mesma lógica do ResponsesScreen
   */
  static async getFormProgressStats(formId) {
    try {
      console.log(`📊 Calculando estatísticas para formulário ${formId}...`);

      // 1. Obter avaliador atual
      const currentAvaliador = this.getDefaultAvaliador();

      if (!currentAvaliador || !currentAvaliador.id || currentAvaliador.id === 'visitor') {
        console.log('⚠️ Nenhum avaliador válido para calcular estatísticas');
        return {
          hasResponses: false,
          progress: { answered: 0, total: 0, percentage: 0 },
          lastModified: null,
          syncStatus: null,
          synced: false,
          syncedAt: null,
          isComplete: false,
        };
      }

      // 2. Buscar TODAS as respostas (igual ResponsesScreen)
      const allResponses = await this.loadFormResponses();

      // 3. Filtrar respostas deste formulário E do avaliador atual
      const formResponses = allResponses.filter(
        response =>
          response.formId === formId &&
          response.avaliador &&
          response.avaliador.id === currentAvaliador.id &&
          response.avaliador.id !== 'visitor'
      );

      console.log(
        `🔍 Encontradas ${formResponses.length} respostas para o formulário ${formId} do avaliador ${currentAvaliador.nome}`
      );

      if (formResponses.length === 0) {
        return {
          hasResponses: false,
          progress: { answered: 0, total: 0, percentage: 0 },
          lastModified: null,
          syncStatus: null,
          synced: false,
          syncedAt: null,
          isComplete: false,
          avaliador: currentAvaliador,
        };
      }

      // 4. Pegar a resposta mais recente
      const latestResponse = formResponses.sort(
        (a, b) => new Date(b.lastModified || b.createdAt) - new Date(a.lastModified || a.createdAt)
      )[0];

      console.log(`📝 Resposta mais recente:`, {
        id: latestResponse.id,
        lastModified: latestResponse.lastModified,
        synced: latestResponse.synced,
      });

      // 5. Calcular progresso DIRETAMENTE das respostas
      const answers = latestResponse.answers || {};
      const answeredCount = Object.keys(answers).filter(key => {
        const answer = answers[key];
        return answer !== null && answer !== undefined && answer !== '';
      }).length;

      console.log(`✅ Respostas encontradas: ${answeredCount}`);

      // 6. Obter total de questões
      let totalQuestions = latestResponse.totalQuestions || 0;

      if (totalQuestions === 0) {
        // Tentar buscar do formulário
        const forms = await this.loadCustomForms();
        const form = forms.find(f => f.id === formId);
        if (form) {
          totalQuestions =
            form.totalQuestions ||
            (form.requirements ? form.requirements.length : 0) ||
            (form.questions ? form.questions.length : 0);
          console.log(`📋 Total de questões do formulário: ${totalQuestions}`);
        }
      }

      const percentage =
        totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

      // CORREÇÃO: Log para debug
      console.log(
        `📊 StorageManager - Progresso calculado para ${formId}: ${answeredCount}/${totalQuestions} = ${percentage}%`
      );

      console.log(`📊 Progresso calculado: ${percentage}% (${answeredCount}/${totalQuestions})`);

      // 7. Determinar status de sincronização
      let syncStatus = 'local-only';
      if (latestResponse.synced === true && latestResponse.syncedAt) {
        syncStatus = 'synced';
      }

      const result = {
        hasResponses: true,
        progress: {
          answered: answeredCount,
          total: totalQuestions,
          percentage: percentage,
        },
        lastModified: latestResponse.lastModified || latestResponse.createdAt,
        syncStatus: syncStatus,
        synced: latestResponse.synced === true && !!latestResponse.syncedAt,
        syncedAt: latestResponse.syncedAt,
        isComplete: latestResponse.isComplete || percentage === 100,
        responseId: latestResponse.id,
        avaliador: currentAvaliador,
        // Adicionar flags úteis
        needsSync: syncStatus === 'local-only' || syncStatus === 'local-newer',
        canSync: syncStatus !== 'cloud-newer',
      };

      console.log(`✅ Estatísticas finais para ${formId}:`, result);

      return result;
    } catch (error) {
      console.error('❌ Erro ao calcular estatísticas de progresso:', error);
      return {
        hasResponses: false,
        progress: { answered: 0, total: 0, percentage: 0 },
        lastModified: null,
        syncStatus: null,
        synced: false,
        syncedAt: null,
        isComplete: false,
      };
    }
  }

  /**
   * NOVA FUNÇÃO: Forçar recálculo completo de estatísticas
   */
  static async forceRecalculateStats(formId) {
    try {
      console.log(`🔄 FORÇANDO recálculo de estatísticas para ${formId}`);

      // Limpar qualquer cache
      const cacheKey = `form_stats_${formId}`;
      this.memoryCache.delete(cacheKey);

      // Recalcular
      const stats = await this.getFormProgressStats(formId);

      // Notificar MÚLTIPLAS vezes para garantir
      this.notifyChangeListeners('form_stats_updated', {
        formId: formId,
        stats: stats,
        timestamp: new Date().toISOString(),
        forceReload: true,
      });

      // Notificar novamente após delay
      setTimeout(() => {
        this.notifyChangeListeners('form_stats_updated', {
          formId: formId,
          stats: stats,
          timestamp: new Date().toISOString(),
          forceReload: true,
        });
      }, 500);

      return stats;
    } catch (error) {
      console.error('❌ Erro ao forçar recálculo:', error);
      throw error;
    }
  }

  /**
   * FUNÇÃO ADICIONADA: Forçar recarga de estatísticas conforme solicitado
   */
  static async forceReloadFormStats(formId) {
    try {
      console.log(`🔄 Forçando recarga de estatísticas para formulário ${formId}`);

      // Limpar cache se existir
      const cacheKey = `form_stats_${formId}`;
      this.memoryCache.delete(cacheKey);

      // Recarregar estatísticas
      const stats = await this.getFormProgressStats(formId);

      // Notificar mudança
      this.notifyChangeListeners('stats_updated', {
        formId: formId,
        stats: stats,
        timestamp: new Date().toISOString(),
      });

      return stats;
    } catch (error) {
      console.error('❌ Erro ao forçar recarga de estatísticas:', error);
      throw error;
    }
  }

  /**
   * NOVA FUNÇÃO: Método para atualizar status de sincronização conforme solicitado
   */
  static async updateResponseSyncStatus(responseId, synced, syncedAt) {
    try {
      const responses = await this.loadFormResponses();
      const index = responses.findIndex(r => r.id === responseId);

      if (index >= 0) {
        responses[index] = {
          ...responses[index],
          synced: synced,
          syncedAt: syncedAt || new Date().toISOString(),
        };

        await this.saveFormResponses(responses);

        // Notificar imediatamente
        this.notifyChangeListeners('response_sync_updated', {
          responseId: responseId,
          formId: responses[index].formId,
          synced: synced,
        });

        return true;
      }

      return false;
    } catch (error) {
      this.log('error', 'Erro ao atualizar status de sincronização:', error);
      return false;
    }
  }

  /**
   * NOVA FUNÇÃO: Obter status de sincronização de uma resposta
   */
  static async getResponseSyncStatus(formId, avaliadorId) {
    try {
      const responses = await this.getResponsesByFormId(formId);
      const avaliadorResponse = responses.find(r => r.avaliador && r.avaliador.id === avaliadorId);

      if (!avaliadorResponse) {
        return { status: 'not-started', hasLocal: false };
      }

      return {
        status: avaliadorResponse.syncStatus || 'local-only',
        hasLocal: true,
        synced: avaliadorResponse.synced || false,
        syncedAt: avaliadorResponse.syncedAt,
        lastModified: avaliadorResponse.lastModified,
      };
    } catch (error) {
      console.error('❌ Erro ao obter status de sincronização:', error);
      return { status: 'error', hasLocal: false };
    }
  }

  /**
   * FUNÇÃO CORRIGIDA: Limpar todos os dados com notificação
   */
  static async clearAllData() {
    try {
      await this.backupCurrentData();

      await AsyncStorage.multiRemove([
        this.KEYS.CUSTOM_FORMS,
        this.KEYS.FORM_RESPONSES,
        this.KEYS.LAST_SYNC,
        this.KEYS.STORAGE_VERSION,
        this.KEYS.DOWNLOADED_FORM_IDS,
      ]);

      this.memoryCache.clear();

      this.log('info', 'Todos os dados foram limpos');

      await this.initialize();

      // CORREÇÃO: Notificar SEM loops - COM DELAY MUITO MAIOR
      setTimeout(() => {
        this.notifyChangeListeners('data_cleared', {
          timestamp: new Date().toISOString(),
          action: 'complete_clear',
        });
      }, 5000); // 5 segundos de delay

      return true;
    } catch (error) {
      this.log('error', 'Erro ao limpar dados:', error);
      return false;
    }
  }

  /**
   * Limpa apenas os arquivos de backup, liberando espaço
   */
  static async clearBackups() {
    try {
      await AsyncStorage.multiRemove([
        this.KEYS.FORM_BACKUP,
        this.KEYS.RESPONSES_BACKUP,
        'deleted_forms',
        'deleted_responses',
        'backup_downloaded_ids',
      ]);

      this.log('info', 'Todos os backups foram removidos');
      return true;
    } catch (error) {
      this.log('error', 'Erro ao limpar backups:', error);
      return false;
    }
  }
  /**
   * Obtém informações sobre o armazenamento
   */
  static async getStorageInfo() {
    try {
      const version = (await AsyncStorage.getItem(this.KEYS.STORAGE_VERSION)) || 'desconhecida';
      const lastSync = (await AsyncStorage.getItem(this.KEYS.LAST_SYNC)) || null;

      const forms = await this.loadCustomForms();
      const responses = await this.loadFormResponses();
      const downloadedIds = await this.loadDownloadedFormIds();

      const formsSize = forms.length > 0 ? JSON.stringify(forms).length : 0;
      const responsesSize = responses.length > 0 ? JSON.stringify(responses).length : 0;

      const pendingUploads = JSON.parse((await AsyncStorage.getItem('pending_uploads')) || '[]');
      const pendingUpdates = JSON.parse((await AsyncStorage.getItem('pending_updates')) || '[]');
      const pendingDeletes = JSON.parse((await AsyncStorage.getItem('pending_deletes')) || '[]');
      const pendingResponseUploads = JSON.parse(
        (await AsyncStorage.getItem('pending_response_uploads')) || '[]'
      );
      const pendingResponseDeletes = JSON.parse(
        (await AsyncStorage.getItem('pending_response_deletes')) || '[]'
      );

      const totalPendingItems =
        pendingUploads.length +
        pendingUpdates.length +
        pendingDeletes.length +
        pendingResponseUploads.length +
        pendingResponseDeletes.length;

      const memoryCacheInfo = {
        hasFormsCache: this.memoryCache.has(this.KEYS.CUSTOM_FORMS),
        hasResponsesCache: this.memoryCache.has(this.KEYS.FORM_RESPONSES),
        hasDownloadedIdsCache: this.memoryCache.has(this.KEYS.DOWNLOADED_FORM_IDS),
        formCacheSize: this.memoryCache.has(this.KEYS.CUSTOM_FORMS)
          ? JSON.stringify(this.memoryCache.get(this.KEYS.CUSTOM_FORMS)).length
          : 0,
        responsesCacheSize: this.memoryCache.has(this.KEYS.FORM_RESPONSES)
          ? JSON.stringify(this.memoryCache.get(this.KEYS.FORM_RESPONSES)).length
          : 0,
        totalCacheItems: Array.from(this.memoryCache.keys()).length,
      };

      // CORREÇÃO: Calcular estatísticas de progresso SEM logs excessivos
      const progressStats = {
        totalForms: forms.length,
        formsWithResponses: 0,
        averageProgress: 0,
        completedForms: 0,
        pendingSyncForms: 0,
      };

      let totalProgress = 0;

      for (const form of forms) {
        const formProgress = await this.getFormProgressStats(form.id);
        if (formProgress.hasResponses) {
          progressStats.formsWithResponses++;
          totalProgress += formProgress.progress.percentage;

          if (formProgress.isComplete) {
            progressStats.completedForms++;
          }

          if (formProgress.syncStatus !== 'synced') {
            progressStats.pendingSyncForms++;
          }
        }
      }

      if (progressStats.formsWithResponses > 0) {
        progressStats.averageProgress = Math.round(
          totalProgress / progressStats.formsWithResponses
        );
      }

      return {
        version,
        lastSync: lastSync ? new Date(lastSync) : null,
        formCount: forms.length,
        responseCount: responses.length,
        downloadedFormCount: downloadedIds.length,
        pendingSyncCount: totalPendingItems,
        storageSize: {
          forms: `${(formsSize / 1024).toFixed(2)} KB`,
          responses: `${(responsesSize / 1024).toFixed(2)} KB`,
          total: `${((formsSize + responsesSize) / 1024).toFixed(2)} KB`,
        },
        memoryCache: memoryCacheInfo,
        progressStats: progressStats,
        listeners: this.changeListeners.size,
        timestamp: new Date(),
      };
    } catch (error) {
      this.log('error', 'Erro ao obter informações de armazenamento:', error);
      return {
        version: 'erro',
        lastSync: null,
        formCount: -1,
        responseCount: -1,
        downloadedFormCount: -1,
        pendingSyncCount: -1,
        storageSize: { forms: 'erro', responses: 'erro', total: 'erro' },
        timestamp: new Date(),
        error: error.message,
      };
    }
  }

  /**
   * FUNÇÃO CORRIGIDA: Limpa dados não essenciais SEM loops
   */
  static async cleanupStorage() {
    try {
      this.log('info', 'Iniciando limpeza de armazenamento não essencial...');

      const lowPriorityKeys = [
        'deleted_forms',
        'deleted_responses',
        'backup_downloaded_ids',
        'cached_forms',
        'form_backup',
        'responses_backup',
        'pending_deletes',
        'pending_uploads',
      ];

      const allKeys = await AsyncStorage.getAllKeys();

      const tempKeys = allKeys.filter(
        key => key.includes('_temp') || key.startsWith('temp_') || key.startsWith('cached_')
      );

      const keysToRemove = [...lowPriorityKeys, ...tempKeys];

      let removedCount = 0;

      for (const key of keysToRemove) {
        try {
          const value = await AsyncStorage.getItem(key);
          if (value !== null) {
            await AsyncStorage.removeItem(key);
            removedCount++;
          }
        } catch (e) {
          this.log('warn', `Erro ao remover ${key}:`, e);
        }
      }

      this.log('info', `Limpeza concluída: ${removedCount} itens removidos`);

      // CORREÇÃO: Notificar SEM loops - COM DELAY MAIOR
      setTimeout(() => {
        this.notifyChangeListeners('storage_cleaned', {
          removedCount,
          action: 'cleanup',
        });
      }, 6000); // 6 segundos de delay

      return removedCount;
    } catch (error) {
      this.log('error', 'Erro na limpeza de armazenamento:', error);
      return 0;
    }
  }

  /**
   * Verificar espaço disponível no armazenamento
   */
  static async checkAvailableSpace() {
    try {
      const keys = await AsyncStorage.getAllKeys();

      let totalSize = 0;
      let largestItems = [];

      for (const key of keys) {
        try {
          const value = await AsyncStorage.getItem(key);
          if (value) {
            const size = value.length;
            totalSize += size;

            largestItems.push({ key, size });
            largestItems.sort((a, b) => b.size - a.size);
            if (largestItems.length > 5) largestItems.pop();
          }
        } catch (e) {
          this.log('warn', `Erro ao verificar tamanho do item ${key}:`, e);
        }
      }

      const estimatedLimit = 5 * 1024 * 1024;
      const percentUsed = Math.round((totalSize / estimatedLimit) * 100);

      this.log(
        'debug',
        `Uso de armazenamento: ${(totalSize / 1024 / 1024).toFixed(2)}MB / ~5MB (${percentUsed}%)`
      );

      let canSave = true;
      if (percentUsed > 90) {
        try {
          const testKey = `test_space_${Date.now()}`;
          const testValue = new Array(1024).join('A');
          await AsyncStorage.setItem(testKey, testValue);
          await AsyncStorage.removeItem(testKey);
        } catch (e) {
          canSave = false;
          this.log('warn', 'Não é possível salvar mais dados:', e);
        }
      }

      return {
        totalSize,
        totalSizeKB: (totalSize / 1024).toFixed(2),
        totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
        percentUsed,
        itemCount: keys.length,
        largestItems,
        canSave,
        estimatedFreeSpace: Math.max(0, estimatedLimit - totalSize),
        estimatedFreeSpaceKB: Math.max(0, ((estimatedLimit - totalSize) / 1024).toFixed(2)),
      };
    } catch (error) {
      this.log('error', 'Erro ao verificar espaço disponível:', error);
      return {
        error: error.message,
        percentUsed: 100,
        canSave: false,
      };
    }
  }

  /**
   * FUNÇÃO CORRIGIDA: Limpar listeners órfãos SEM causar loops
   */
  static clearOrphanListeners() {
    const sizeBefore = this.changeListeners.size;
    this.changeListeners.clear();

    // Limpar notificações pendentes também
    this.notificationQueue = [];
    this.isNotifying = false;

    this.log('info', `${sizeBefore} listeners removidos durante limpeza`);
  }

  /**
   * FUNÇÃO CORRIGIDA: Obter status de todos os formulários com progresso SEM logs excessivos
   */
  static async getAllFormsWithProgress() {
    try {
      const forms = await this.loadCustomForms();
      const formsWithProgress = [];

      for (const form of forms) {
        const progressStats = await this.getFormProgressStats(form.id);
        formsWithProgress.push({
          ...form,
          progressStats,
        });
      }

      return formsWithProgress;
    } catch (error) {
      this.log('error', 'Erro ao obter formulários com progresso:', error);
      return [];
    }
  }

  /**
   * NOVA FUNÇÃO: Debounce para operações de salvamento
   */
  static debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * NOVA FUNÇÃO: Throttle para notificações SEM causar loops
   */
  static throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * NOVA FUNÇÃO: Limpar cache de memória periodicamente
   */
  static clearMemoryCache() {
    try {
      const sizeBeforeKB = this.getMemoryCacheSize();
      this.memoryCache.clear();

      this.log('info', `Cache de memória limpo: ${sizeBeforeKB.toFixed(2)} KB liberados`);

      // CORREÇÃO: Notificar SEM loops - COM DELAY MUITO MAIOR
      setTimeout(() => {
        this.notifyChangeListeners('memory_cache_cleared', {
          freedSize: sizeBeforeKB,
          action: 'memory_cleanup',
        });
      }, 7000); // 7 segundos de delay

      return sizeBeforeKB;
    } catch (error) {
      this.log('error', 'Erro ao limpar cache de memória:', error);
      return 0;
    }
  }

  /**
   * NOVA FUNÇÃO: Obter tamanho do cache de memória
   */
  static getMemoryCacheSize() {
    try {
      let totalSize = 0;

      this.memoryCache.forEach((value, key) => {
        try {
          const serialized = JSON.stringify(value);
          totalSize += serialized.length;
        } catch (e) {
          // Ignorar itens que não podem ser serializados
        }
      });

      return totalSize / 1024; // Retornar em KB
    } catch (error) {
      this.log('error', 'Erro ao calcular tamanho do cache:', error);
      return 0;
    }
  }

  /**
   * NOVA FUNÇÃO: Verificação periódica de integridade
   */
  static async performHealthCheck() {
    try {
      this.log('debug', 'Executando verificação de integridade...');

      const healthReport = {
        timestamp: new Date().toISOString(),
        storage: {
          accessible: true,
          corrupted: false,
          needsRepair: false,
        },
        cache: {
          size: this.getMemoryCacheSize(),
          itemCount: this.memoryCache.size,
        },
        listeners: {
          count: this.changeListeners.size,
          queuedNotifications: this.notificationQueue.length,
        },
        performance: {
          lastNotificationTime: this.lastNotificationTime,
          throttleActive: Date.now() - this.lastNotificationTime < this.NOTIFICATION_THROTTLE,
          isNotifying: this.isNotifying,
        },
      };

      // Testar acesso ao AsyncStorage
      try {
        const testKey = `health_check_${Date.now()}`;
        await AsyncStorage.setItem(testKey, 'test');
        await AsyncStorage.getItem(testKey);
        await AsyncStorage.removeItem(testKey);
      } catch (storageError) {
        healthReport.storage.accessible = false;
        this.log('error', 'Problema de acesso ao AsyncStorage:', storageError);
      }

      // Verificar integridade dos dados principais
      try {
        const forms = await this.loadCustomForms();
        const responses = await this.loadFormResponses();

        if (!Array.isArray(forms) || !Array.isArray(responses)) {
          healthReport.storage.corrupted = true;
          healthReport.storage.needsRepair = true;
        }
      } catch (integrityError) {
        healthReport.storage.corrupted = true;
        healthReport.storage.needsRepair = true;
        this.log('error', 'Dados corrompidos detectados:', integrityError);
      }

      // Limpar cache se estiver muito grande (> 5MB)
      if (healthReport.cache.size > 5 * 1024) {
        this.log('warn', 'Cache de memória muito grande, limpando...');
        this.clearMemoryCache();
        healthReport.cache.cleaned = true;
      }

      // Reparar se necessário
      if (healthReport.storage.needsRepair) {
        this.log('warn', 'Reparando armazenamento corrompido...');
        await this.repairStorage();
        healthReport.storage.repaired = true;
      }

      this.log('debug', 'Verificação de integridade concluída');
      return healthReport;
    } catch (error) {
      this.log('error', 'Erro na verificação de integridade:', error);
      return {
        timestamp: new Date().toISOString(),
        error: error.message,
        storage: { accessible: false, corrupted: true, needsRepair: true },
      };
    }
  }

  /**
   * NOVA FUNÇÃO: Configurar limpeza automática
   */
  static setupAutomaticCleanup(intervalMinutes = 30) {
    // Limpar intervalo anterior se existir
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    // Configurar novo intervalo
    this.cleanupInterval = setInterval(
      async () => {
        try {
          const healthReport = await this.performHealthCheck();

          // Se o cache estiver muito grande, limpar
          if (healthReport.cache && healthReport.cache.size > 2 * 1024) {
            this.clearMemoryCache();
          }

          // Se houver muitos listeners, limpar órfãos
          if (healthReport.listeners && healthReport.listeners.count > 5) {
            this.clearOrphanListeners();
          }
        } catch (error) {
          this.log('error', 'Erro na limpeza automática:', error);
        }
      },
      intervalMinutes * 60 * 1000
    );

    this.log('info', `Limpeza automática configurada (a cada ${intervalMinutes} minutos)`);
  }

  /**
   * NOVA FUNÇÃO: Parar limpeza automática
   */
  static stopAutomaticCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
      this.log('info', 'Limpeza automática parada');
    }
  }

  /**
   * NOVA FUNÇÃO: Configurar nível de log
   */
  static setLogLevel(level) {
    if (['minimal', 'normal', 'debug'].includes(level)) {
      this.logLevel = level;
      this.log('info', `Nível de log alterado para: ${level}`);
    } else {
      this.log('warn', `Nível de log inválido: ${level}. Use 'minimal', 'normal' ou 'debug'`);
    }
  }

  /**
   * NOVA FUNÇÃO: Obter estatísticas de performance
   */
  static getPerformanceStats() {
    return {
      listenersCount: this.changeListeners.size,
      queuedNotifications: this.notificationQueue.length,
      lastNotificationTime: this.lastNotificationTime,
      isNotifying: this.isNotifying,
      notificationThrottle: this.NOTIFICATION_THROTTLE,
      memoryCacheSize: this.getMemoryCacheSize(),
      memoryCacheItems: this.memoryCache.size,
      logLevel: this.logLevel,
      logCount: this.logCount,
      maxLogsPerMinute: this.MAX_LOGS_PER_MINUTE,
      notificationCount: this.notificationCount,
      maxNotificationsPerMinute: this.MAX_NOTIFICATIONS_PER_MINUTE,
    };
  }

  /**
   * NOVA FUNÇÃO: Reset completo do sistema de notificações SEM causar loops
   */
  static resetNotificationSystem() {
    // Parar qualquer processamento em andamento
    this.isNotifying = false;

    // Limpar notificações pendentes
    this.notificationQueue = [];

    // Reset timestamps e contadores
    this.lastNotificationTime = 0;
    this.notificationCount = 0;
    this.lastNotificationReset = Date.now();

    this.log('info', 'Sistema de notificações resetado');
  }

  /**
   * FUNÇÃO CORRIGIDA: Obter todas as atualizações pendentes (sem logs excessivos)
   */
  static async getPendingUpdates() {
    try {
      const forms = await this.loadCustomForms();
      const responses = await this.loadFormResponses();

      const pendingForms = forms.filter(form => !form.synced);
      const pendingResponses = responses.filter(response => !response.synced);

      return {
        forms: pendingForms,
        responses: pendingResponses,
        totalPending: pendingForms.length + pendingResponses.length,
      };
    } catch (error) {
      this.log('error', 'Erro ao obter atualizações pendentes:', error);
      return {
        forms: [],
        responses: [],
        totalPending: 0,
      };
    }
  }

  /**
   * NOVA FUNÇÃO: Validar integridade de uma resposta específica
   */
  static validateResponse(response) {
    if (!response) return false;
    if (!response.id || !response.formId) return false;
    if (!response.avaliador || !response.avaliador.id) return false;
    if (!response.answers || typeof response.answers !== 'object') return false;

    return true;
  }

  /**
   * NOVA FUNÇÃO: Validar integridade de um formulário específico
   */
  static validateForm(form) {
    if (!form) return false;
    if (!form.id || !form.title) return false;
    if (!form.requirements || !Array.isArray(form.requirements)) return false;

    return true;
  }

  /**
   * NOVA FUNÇÃO: Obter estatísticas detalhadas de um formulário
   */
  static async getDetailedFormStats(formId) {
    try {
      const responses = await this.getResponsesByFormId(formId);
      const progressStats = await this.getFormProgressStats(formId);

      return {
        formId,
        responseCount: responses.length,
        progressStats,
        responses: responses.map(r => ({
          id: r.id,
          avaliador: r.avaliador?.nome || 'Desconhecido',
          progress: this.calculateResponseProgress(r),
          lastModified: r.lastModified,
          isComplete: r.isComplete,
          synced: r.synced,
        })),
      };
    } catch (error) {
      this.log('error', `Erro ao obter estatísticas detalhadas para formulário ${formId}:`, error);
      return null;
    }
  }

  /**
   * NOVA FUNÇÃO: Reparar respostas corrompidas
   */
  static async repairCorruptedResponses() {
    try {
      this.log('info', 'Iniciando reparo de respostas corrompidas...');

      const responses = await this.loadFormResponses();
      const validResponses = [];
      let repairedCount = 0;
      let removedCount = 0;

      for (const response of responses) {
        if (this.validateResponse(response)) {
          validResponses.push(response);
        } else {
          this.log('warn', `Resposta corrompida encontrada: ${response.id || 'ID desconhecido'}`);

          // Tentar reparar
          const repairedResponse = this.attemptResponseRepair(response);
          if (repairedResponse && this.validateResponse(repairedResponse)) {
            validResponses.push(repairedResponse);
            repairedCount++;
          } else {
            removedCount++;
          }
        }
      }

      if (repairedCount > 0 || removedCount > 0) {
        await this.saveFormResponses(validResponses);
        this.log('info', `Reparo concluído: ${repairedCount} reparadas, ${removedCount} removidas`);
      }

      return { repairedCount, removedCount };
    } catch (error) {
      this.log('error', 'Erro ao reparar respostas corrompidas:', error);
      return { repairedCount: 0, removedCount: 0 };
    }
  }

  /**
   * NOVA FUNÇÃO: Tentar reparar uma resposta específica
   */
  static attemptResponseRepair(response) {
    try {
      const defaultAvaliador = this.getDefaultAvaliador() || {
        id: 'repaired_user',
        nome: 'Usuário Reparado',
        login: 'repaired@ebserh.gov.br',
        level: 'membro',
      };

      return {
        id: response.id || `repaired_${Date.now()}`,
        formId: response.formId || 'unknown_form',
        answers: response.answers || {},
        findings: response.findings || {},
        savedFindings: response.savedFindings || {},
        notApplicableReasons: response.notApplicableReasons || {},
        attachments: response.attachments || {},
        avaliador: response.avaliador || defaultAvaliador,
        createdAt: response.createdAt || new Date().toISOString(),
        lastModified: response.lastModified || new Date().toISOString(),
        isComplete: response.isComplete || false,
        synced: false,
        syncStatus: 'local-only',
      };
    } catch (error) {
      this.log('error', 'Erro ao tentar reparar resposta:', error);
      return null;
    }
  }

  /**
   * NOVA FUNÇÃO: Forçar sincronização de dados críticos
   */
  static async forceCriticalDataSync() {
    try {
      this.log('info', 'Forçando sincronização de dados críticos...');

      // Verificar e reparar dados corrompidos
      await this.repairCorruptedResponses();

      // Limpar listeners órfãos
      this.clearOrphanListeners();

      // Reset sistema de notificações
      this.resetNotificationSystem();

      // Limpar cache de memória se muito grande
      const cacheSize = this.getMemoryCacheSize();
      if (cacheSize > 1024) {
        // > 1MB
        this.clearMemoryCache();
      }

      this.log('info', 'Sincronização de dados críticos concluída');
      return true;
    } catch (error) {
      this.log('error', 'Erro na sincronização de dados críticos:', error);
      return false;
    }
  }
}

export default StorageManager;
